import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Field } from 'redux-form';
import Box from '@mui/material/Box';

import VariantInputGroupField from 'bento-ordering/menus/components/VariantInputGroupField';
import StyledVariantBox from 'components/StyledVariantBox';
import {
  getVariantName,
  getVariantPrice,
  getCheckboxOrRadio,
  RADIO_BUTTON,
  getVariantLabel,
  QUANTITY
} from 'helpers/forms/menuItem';
import {
  PRODUCT_VARIANT_FIELD_NAME,
  PRODUCT_VARIANT_SECTION_NAME
} from 'constants/menu/menuItemDetailForm';
import { getShouldRenderProductVariantsSection } from 'bento-ordering/menus/selectors';
import { useHasInvestorDayFeatureFlag } from 'bento-ordering/utils/featureFlags/hasInvestorDayFeatureFlag';

const VariantGroupForm = ({ form, modifierGroups, variants }) => {
  const shouldRenderProductVariantsSection = getShouldRenderProductVariantsSection(
    variants
  );

  const values = get(form.menuItemDetail, 'values', {});

  const hasInvestorDayFeature = useHasInvestorDayFeatureFlag();

  /*
   * Parse the variant data to fit our radioButtonGroup and
   * checkbox group component
   */
  const getParsedVariantOptions = options => {
    const parsedOptions = options
      .sort((a, b) => a.order - b.order)
      .reduce((acc, currentValue) => {
        const price = getVariantPrice(currentValue.price_variance);
        const isCurrentItemDisabled = !!currentValue.disabled;

        acc.push({
          label: isCurrentItemDisabled
            ? `${currentValue.name} (sold out)`
            : currentValue.name,
          value: currentValue.slug,
          price,
          disabled: isCurrentItemDisabled
        });
        return acc;
      }, []);

    return [
      ...parsedOptions.filter(option => !option.disabled),
      ...parsedOptions.filter(option => option.disabled)
    ];
  };

  const getParsedProductVariants = () => {
    return variants.map(
      ({ name, price, id, calories, calories_max: caloriesMax }) => ({
        label: getVariantLabel(name, calories, caloriesMax),
        value: id,
        price: getVariantPrice(price)
      })
    );
  };

  /**
   * Track if the number we selected the max selections
   * for a given checkbox. This function counts the number
   * of selected values for the specific checkbox variant
   * in redux form.
   */
  const isMaxVariantOptionsSelected = (maxQuantity, slug) => {
    if (!values || Object.values(values).length === 0 || !values[slug]) {
      return false;
    }

    const selected = Object.values(values[slug]);
    if (!selected) {
      return false;
    }
    const trueValues = selected.filter(option => option).length;
    return trueValues >= maxQuantity;
  };

  /**
   * Render radio inputs or checkbox inputs base on the modifierGroups returned.
   * We are following a similar structure to the catering store.
   * The modifierGroups array represent each variant a user can have for their item.
   * The options array under a modifierGroups are the modifiers for that particular variant.
   */
  const renderVariantGroups = () => {
    if (modifierGroups) {
      return modifierGroups.map(modifierGroup => {
        const {
          display_name,
          name,
          slug,
          id,
          max_qty,
          min_qty,
          options,
          helper_text,
          field_type
        } = modifierGroup;

        const optionsList = getParsedVariantOptions(options);
        const maxQty = max_qty || optionsList.length;
        const variantName = getVariantName(
          display_name || name,
          parseInt(min_qty),
          parseInt(maxQty)
        );

        const fieldType =
          field_type === QUANTITY
            ? QUANTITY
            : getCheckboxOrRadio(min_qty, maxQty);

        return (
          <StyledVariantBox
            key={name + id}
            borderBottom="1px solid"
            data-testid={`variant-field-${id}`}
            aria-label={`This is a group of item modifiers to select: ${name}`}
          >
            <Box pt={2.5} pb={2}>
              <Field
                key={id}
                title={variantName}
                name={slug}
                component={VariantInputGroupField}
                helperText={helper_text}
                fieldType={fieldType}
                disabled={
                  min_qty === 1 && max_qty === 1
                    ? false
                    : isMaxVariantOptionsSelected(maxQty, slug)
                }
                list={optionsList}
                required={min_qty > 0}
                maxQty={maxQty}
                minQty={min_qty}
                hasInvestorDayFeature={hasInvestorDayFeature}
              />
            </Box>
          </StyledVariantBox>
        );
      });
    }

    return null;
  };

  const renderProductVariants = () => {
    if (!shouldRenderProductVariantsSection) {
      return null;
    }

    const variantsList = getParsedProductVariants(variants);

    return (
      <StyledVariantBox
        key={PRODUCT_VARIANT_FIELD_NAME}
        borderBottom="1px solid"
        data-testid={PRODUCT_VARIANT_FIELD_NAME}
        aria-label={`This is a group of item modifiers to select: ${PRODUCT_VARIANT_SECTION_NAME}`}
      >
        <Box pt={2.5} pb={2}>
          <Field
            title={PRODUCT_VARIANT_SECTION_NAME}
            name={PRODUCT_VARIANT_FIELD_NAME}
            component={VariantInputGroupField}
            helperText=""
            fieldType={RADIO_BUTTON}
            disabled={false}
            list={variantsList}
            required={true}
            isSizeSection
            hasInvestorDayFeature={hasInvestorDayFeature}
          />
        </Box>
      </StyledVariantBox>
    );
  };

  return (
    <>
      {renderProductVariants()}
      {renderVariantGroups()}
    </>
  );
};

VariantGroupForm.propTypes = {
  form: PropTypes.shape({
    menuItemDetail: PropTypes.shape({ values: PropTypes.shape({}) })
  }),
  modifierGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      id: PropTypes.number,
      max_qty: PropTypes.number,
      min_qty: PropTypes.number,
      helper_text: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slug: PropTypes.string,
          price: PropTypes.number
        })
      )
    })
  ),
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.number,
      order: PropTypes.number,
      price: PropTypes.string,
      weight: PropTypes.number,
      calories: PropTypes.number,
      product: PropTypes.number
    })
  )
};

export default VariantGroupForm;
